import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type { ApiError, AuthMeResponse, DeliverablesResponse, HomeResponse, HomeVideoDto, JourneyMode, Locale, SectionDto, SectionKey } from "../shared/dto";
import { VideoGate } from "./VideoGate";
import { browserLocale, rememberLocale, translate, translateCount } from "./i18n";
import { setDocLocale } from "./doc/doc-locale";
import { clearQuestionnaireDrafts, QuestionnaireRunner } from "./QuestionnaireRunner";
import { M4SectionView, type M4Surface } from "./M4Sections";
import { CountdownCells } from "./Countdown";
import { M5bSectionView, StrategyWaitingState, type M5bSurface } from "./DeliverySections";
import { requestWizardFocus } from "./sections/app-wizard/wizard-shared";
import { NotificationSettings } from "./NotificationSettings";
import { NotificationCenter, type InboxSurface } from "./NotificationCenter";
import { PartnerControl } from "./PartnerControl";
import { ProfileView } from "./ProfileView";
import { WorkingAvatar } from "./WorkingAvatar";
import { StaffConsole } from "./StaffConsole";
import { LanguagePreference } from "./LanguagePreference";

// The switch used to be defined here; it moved to its own module so the
// staff console can render it too (see src/LanguagePreference.tsx). It stays
// exported from App for the call sites that already import it from here.
export { LanguagePreference };
import { ErrorBoundary } from "./ErrorBoundary";
import { setReadonlyMode } from "./readonly";
import {
  announceIdentityChange,
  identityFingerprint,
  setStaleCsrfHandler,
  startIdentityWatch,
  type MountedIdentity
} from "./identity-sync";
import { ImpersonationBar } from "./ImpersonationBar";
import { formatDateTime, intlLanguage } from "./date-format";
import {
  buildRailSteps,
  RAIL_STEP_KEYS,
  clientOpenSearch,
  clientOpenTarget,
  clientStepLocked,
  currentIncompleteRailStep,
  journeyAxesOf,
  nextRailStep,
  railInteractionForStep,
  railStepEverDrawn,
  railStepPosition,
  railTargetForStep,
  reviewReadyStep,
  simulateClientSnapshot,
  simulateRailSteps,
  STEP_HEADING_KEYS,
  STEP_EYEBROW_RAIL_KEYS,
  type ClientOpenTarget,
  type RailStep,
  type RailStepKey
} from "./rail-model";
import { railProgressFraction, type RailStepProgress } from "../shared/rail-substeps";
import { homeShowsFinished, journeyFinished as journeyEnded } from "./journey-end";
import { defaultLandingTarget } from "./landing";
import { ClosedStep, LockedStep } from "./sections/ClosedStep";
import { VideoStepPanel, type VideoStepKey } from "./sections/CallStep";
import { VideoPreparingCard } from "./VideoGate";
import { StepCelebration } from "./sections/StepCelebration";
import { acknowledgeStep, ensureSeenBaseline, freshUnlockedKeys, writeSeenUnlocked } from "./rail-unlock-pulse";
import { actionIssueFrom, actionIssueFromThrown, actionIssueLocaleKey, type ActionIssue } from "./action-error";

// The video step lives in ./sections/CallStep (F35 -> F81). Re-exported here
// so every existing import site keeps working after the split.
export { VideoStepPanel } from "./sections/CallStep";

type DashboardSurface = "home" | "onboarding_call" | "marketing_call" | "profile" | "account" | M4Surface | M5bSurface;

export function notificationFocusSelector(target: InboxSurface): string {
  if (target === "q1" || target === "q2") return '[data-question-key]:not(.mobile-hidden) h2';
  if (target === "home") return "#dashboard-title";
  if (target === "account") return "#account-title";
  if (target === "onboarding_call" || target === "marketing_call") return "#call-focus-title";
  if (target === "profile") return ".confirmed-profile-intro h1, .profile-view h1";
  return ".dashboard-main h1, .dashboard-main h2";
}

export function focusNotificationDestination(
  root: { querySelector<T extends Element = Element>(selectors: string): T | null },
  target: InboxSurface
): boolean {
  const element = root.querySelector<HTMLElement>(notificationFocusSelector(target));
  if (!element) return false;
  element.tabIndex = -1;
  element.focus({ preventScroll: true });
  return true;
}

function dashboardSurfaceForTarget(target: ClientOpenTarget): DashboardSurface {
  if (target === "q1" || target === "q2") return "home";
  // F135: `app:apple` is its own surface now. It used to collapse onto the
  // whole-track `app` surface, which is what put the questionnaire card and
  // the release checklist on the Apple step's screen.
  return target as DashboardSurface;
}

export function resetSurfaceScroll() {
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
}

export function clientStepTranslation(
  key: string,
  steps: RailStep[],
  t: (key: string) => string
): string {
  const railKey = STEP_EYEBROW_RAIL_KEYS[key];
  if (!railKey) return t(key);
  const position = railStepPosition(steps, railKey);
  if (!position) return t(key);
  return t("phase.progress")
    .replace("{current}", String(position.current))
    .replace("{total}", String(position.total));
}

type AuthState =
  | { kind: "loading" }
  | { kind: "guest" }
  | { kind: "authenticated"; data: AuthMeResponse }
  | { kind: "error" };

export function App() {
  const [locale, setLocale] = useState<Locale>(() => browserLocale());
  const [auth, setAuth] = useState<AuthState>({ kind: "loading" });
  const t = useCallback((key: string) => translate(locale, key), [locale]);

  const loadSession = useCallback(async () => {
    setAuth({ kind: "loading" });
    try {
      const response = await fetch("/api/auth/me", { credentials: "include" });
      if (response.status === 401) {
        setAuth({ kind: "guest" });
        return;
      }
      if (!response.ok) throw new Error("auth_status_failed");
      const data = (await response.json()) as AuthMeResponse;
      setLocale(data.user.locale);
      setAuth({ kind: "authenticated", data });
    } catch {
      setAuth({ kind: "error" });
    }
  }, []);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = t("meta.title");
    rememberLocale(locale);
    // The document renderer draws its own chrome (copy buttons, the timeline
    // label) from a dozen call sites that never pass `t`; it reads the locale
    // the app has already decided instead. See doc/doc-locale.ts.
    setDocLocale(locale);
  }, [locale, t]);

  // Mirror the server's impersonation contract in the browser (F138e): the
  // gate runs in the SAME mode the session carries, so a write the server
  // would take is not blocked here and a write it would refuse never leaves
  // the page. Every refusal is named, on the bar.
  const gateMode = auth.kind === "authenticated" ? auth.data.impersonation?.mode ?? null : null;
  useEffect(() => {
    setReadonlyMode(gateMode);
    return () => setReadonlyMode(null);
  }, [gateMode]);

  // Tabsync: the tab records the identity it RENDERS and the watcher reloads
  // it the moment the browser's cookie demonstrably belongs to a different
  // one (src/identity-sync.ts - the incident, the triggers, the law). A ref,
  // not a dependency: the watch installs once and reads the current render.
  const mountedIdentityRef = useRef<MountedIdentity>(null);
  mountedIdentityRef.current = auth.kind === "authenticated"
    ? identityFingerprint(auth.data)
    : auth.kind === "guest" ? "guest" : null;
  useEffect(() => startIdentityWatch(() => mountedIdentityRef.current), []);

  // 🔴 A write refused because the token belongs to a session the server has
  // since renewed repairs itself: take a fresh token and keep the surface
  // mounted. NOT `loadSession` - that one goes through `kind: "loading"` and
  // would unmount the page under a half-written note or a recording in
  // progress. Only the auth data is swapped, so every writer picks the new
  // token up through its props on the next attempt.
  const refreshCsrfToken = useCallback(async () => {
    try {
      const response = await fetch("/api/auth/me", { credentials: "include" });
      if (!response.ok) return;
      const data = (await response.json()) as AuthMeResponse;
      setAuth((current) =>
        current.kind === "authenticated" && current.data.csrfToken !== data.csrfToken
          ? { kind: "authenticated", data }
          : current);
    } catch {
      // An unreachable /me says nothing about the token; the refusal the
      // surface is already showing stands.
    }
  }, []);
  useEffect(() => {
    setStaleCsrfHandler(() => void refreshCsrfToken());
    return () => setStaleCsrfHandler(null);
  }, [refreshCsrfToken]);

  return (
    <div className="app-shell">
      {auth.kind === "authenticated" && auth.data.impersonation && (
        <ImpersonationBar
          data={auth.data}
          t={t}
          onExit={(outcome) => {
            // A restored console goes back to it; a console that expired in
            // the meantime says so on the login screen instead of dropping
            // the partner there with no explanation (audit risk 5).
            window.location.assign(outcome === "restored" ? "/" : "/?impersonation=console_lost");
          }}
        />
      )}
      {auth.kind === "loading" && <LoadingScreen t={t} />}
      {auth.kind === "guest" && <LoginScreen t={t} locale={locale} />}
      {auth.kind === "authenticated" && (
        <Dashboard
          data={auth.data}
          locale={locale}
          t={t}
          onLocaleChange={setLocale}
          onLoggedOut={() => setAuth({ kind: "guest" })}
          onReload={loadSession}
        />
      )}
      {auth.kind === "error" && <ErrorScreen t={t} retry={loadSession} />}
    </div>
  );
}

// F138e: ImpersonationBanner and ReadonlyToast both moved into
// src/ImpersonationBar.tsx. The banner said one sentence and scrolled away;
// the toast said the same refusal a second time, in a second place. The bar
// is now the ONE surface that states the preview and answers a refused write
// by name, and it is on screen the whole time.

function WelcomeGuide({ t, role, currentStep, returning, onDone }: {
  t: (key: string) => string;
  role: "owner" | "collaborator";
  currentStep: RailStep | null;
  returning: boolean;
  onDone: () => void;
}) {
  const currentStepTitle = currentStep ? t(STEP_HEADING_KEYS[currentStep.key]) : t("home.waiting");
  // The guide must describe the shell the client actually sees: the phone has
  // the journey in the top menu, the partner card under the content and the
  // current task in the main area, not "vlevo/vpravo/dole" (audit a1-r4-01).
  const [mobileShell] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  const guideCopy = (key: string) => t(mobileShell ? `${key}Mobile` : key);
  const steps = [
    // The Buddy belongs to the OWNER; an invited collaborator helps build it,
    // so the intro must not promise them "tvůj Buddy" (night finding 25.7.).
    {
      title: t(returning ? "home.returningTitle" : "home.welcomeTitle"),
      copy: t(returning
        ? role === "collaborator" ? "home.returningIntroCollaborator" : "home.returningIntro"
        : role === "collaborator" ? "home.welcomeIntroCollaborator" : "home.welcomeIntro")
        .replace("{step}", currentStepTitle)
    },
    { title: t("home.guide.step2Title"), copy: guideCopy("home.guide.step2Copy") },
    { title: t("home.guide.step3Title"), copy: guideCopy("home.guide.step3Copy") },
    { title: t("home.guide.step4Title"), copy: guideCopy("home.guide.step4Copy") }
  ];
  const [step, setStep] = useState(0);
  const last = step === steps.length - 1;
  const cardRef = useRef<HTMLDivElement | null>(null);
  // Move focus into the dialog on open and let Escape dismiss it, so keyboard
  // and screen-reader users are not trapped behind a modal with no exit.
  useEffect(() => {
    cardRef.current?.focus();
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onDone();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDone]);
  return (
    <div className="welcome-overlay" role="dialog" aria-modal="true" aria-labelledby="welcome-title">
      <div className="welcome-card" ref={cardRef} tabIndex={-1}>
        <button className="welcome-skip text-button compact-button" type="button" onClick={onDone}>{t("home.guide.skip")}</button>
        <img className="brand-logo" src="/assets/buddypro-logo.png" alt={t("common.brandAlt")} />
        <h2 id="welcome-title">{steps[step].title}</h2>
        <p>{steps[step].copy}</p>
        <div className="welcome-dots" aria-hidden="true">
          {steps.map((_, index) => <span key={index} className={index === step ? "active" : ""} />)}
        </div>
        <div className="welcome-actions">
          {step > 0 && <button className="text-button compact-button" type="button" onClick={() => setStep(step - 1)}>{t("home.guide.back")}</button>}
          <button className="primary-button compact-button" type="button" onClick={() => last ? onDone() : setStep(step + 1)}>
            {last ? t(returning ? "home.returningCta" : "home.welcomeCta") : t("home.guide.next")}
          </button>
        </div>
      </div>
    </div>
  );
}

function LoginScreen({ t, locale }: { t: (key: string) => string; locale: Locale }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "accepted" | "error">("idle");
  const [issue, setIssue] = useState<ActionIssue>("generic");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState("loading");
    try {
      const response = await fetch("/api/auth/request-link", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, locale })
      });
      if (!response.ok) {
        setIssue(await actionIssueFrom(response));
        setState("error");
        return;
      }
      setState("accepted");
    } catch {
      setIssue(actionIssueFromThrown());
      setState("error");
    }
  }

  // F138e: a partner whose console session expired while they were previewing
  // a client lands here. Saying WHY beats a login screen that appeared for no
  // reason (audit risk 5).
  const consoleLost = typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("impersonation") === "console_lost";

  return (
    <main className="login-shell">
      <section className="login-card" aria-labelledby="login-title">
        <img className="brand-logo" src="/assets/buddypro-logo.png" alt={t("common.brandAlt")} />
        <p className="eyebrow blue-eyebrow">{t("login.eyebrow")}</p>
        <h1 id="login-title">{t("login.title")}</h1>
        <p className="lead">{t("login.description")}</p>
        {consoleLost && <p className="message error-message">{t("impersonation.consoleLost")}</p>}
        <form onSubmit={submit} className="login-form">
          <label htmlFor="email">{t("login.emailLabel")}</label>
          <input
            id="email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder={t("login.emailPlaceholder")}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            disabled={state === "loading" || state === "accepted"}
          />
          <button className="primary-button" type="submit" disabled={state === "loading" || state === "accepted"}>
            {state === "loading" ? t("login.submitting") : t("login.submit")}
          </button>
        </form>
        <div className="message-space" aria-live="polite">
          {state === "accepted" && (
            <>
              <p className="message success-message">{t("login.accepted")}</p>
              <button className="text-button compact-button" type="button" onClick={() => setState("idle")}>{t("login.tryAnother")}</button>
            </>
          )}
          {state === "error" && <p className="message error-message">{t(actionIssueLocaleKey(issue))}</p>}
        </div>
        <p className="privacy-copy">{t("login.privacy")}</p>
      </section>
    </main>
  );
}

function Dashboard(dashboardProps: {
  data: AuthMeResponse;
  locale: Locale;
  t: (key: string) => string;
  onLocaleChange: (locale: Locale) => void;
  onLoggedOut: () => void;
  onReload: () => Promise<void>;
}) {
  const [switchTarget, setSwitchTarget] = useState(
    dashboardProps.data.activeClientId ?? dashboardProps.data.memberships[0]?.clientId ?? ""
  );
  const [switching, setSwitching] = useState(false);
  const [actionError, setActionError] = useState<ActionIssue | null>(null);
  const [home, setHome] = useState<
    { kind: "loading" } |
    { kind: "ready"; data: HomeResponse; delivery: DeliverablesResponse } |
    { kind: "error" }
  >({
    kind: "loading"
  });
  // F64 (Pavel 2026-08-06: "Dotaznik na me mluvi v zenskem rode, i kdyz jsem
  // muz"). Every client surface hangs under this component and takes its `t`
  // from here, so conjugating the copy is one swap of that closure rather than
  // a gender prop threaded through seventy call sites. Before /api/home
  // answers, the client sees the loading screen, never a wrongly gendered
  // sentence; an unknown gender stays neutral ("spustil/a").
  const clientGender = home.kind === "ready" ? home.data.client.gender : null;
  const genderedT = useCallback(
    (key: string) => translate(dashboardProps.locale, key, clientGender),
    [dashboardProps.locale, clientGender]
  );
  const props = useMemo(
    () => ({ ...dashboardProps, t: genderedT }),
    [dashboardProps, genderedT]
  );
  const [missedOpen, setMissedOpen] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [profileHistoryOpen, setProfileHistoryOpen] = useState(false);
  const initialClientTarget = useRef<ClientOpenTarget>(clientOpenTarget(window.location.search)).current;
  const [activeClientTarget, setActiveClientTarget] = useState<ClientOpenTarget>(initialClientTarget);
  const [activeQuestionnaire, setActiveQuestionnaire] = useState<"q1" | "q2" | null>(() => {
    return initialClientTarget === "q1" || initialClientTarget === "q2" ? initialClientTarget : null;
  });
  const [surface, setSurfaceState] = useState<DashboardSurface>(() => dashboardSurfaceForTarget(initialClientTarget));
  const [unlockReveal, setUnlockReveal] = useState<{ section: "q1" | "q2"; already: boolean } | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const section = params.get("unlocked");
    if (section !== "q1" && section !== "q2") return null;
    return { section, already: params.get("status") === "already_redeemed" };
  });
  const welcomeKey = `launchpad-welcome-${props.data.activeClientId ?? "none"}`;
  const [welcomeOpen, setWelcomeOpen] = useState<boolean>(() => {
    try { return !window.localStorage.getItem(welcomeKey); } catch { return false; }
  });
  const [mobileLayout, setMobileLayout] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  const dashboardMainRef = useRef<HTMLElement | null>(null);
  const pendingInboxFocusRef = useRef<InboxSurface | null>(null);
  const [inboxFocusRequest, setInboxFocusRequest] = useState(0);
  // F39 pulse ledger: which open steps the client has already acknowledged.
  const [seenUnlocked, setSeenUnlocked] = useState<string[] | null>(null);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    const update = () => setMobileLayout(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  function writeClientLocation(target: ClientOpenTarget, mode: "push" | "replace") {
    setActiveClientTarget(target);
    const next = `${window.location.pathname}${clientOpenSearch(target)}${window.location.hash}`;
    window.history[mode === "push" ? "pushState" : "replaceState"](null, "", next);
  }

  function restoreClientTarget(target: ClientOpenTarget) {
    setActiveClientTarget(target);
    if (target === "q1" || target === "q2") {
      setSurfaceState("home");
      setActiveQuestionnaire(target);
      return;
    }
    setActiveQuestionnaire(null);
    setSurfaceState(dashboardSurfaceForTarget(target));
  }

  function setSurface(target: ClientOpenTarget, mode: "push" | "replace" = "push") {
    restoreClientTarget(target);
    writeClientLocation(target, mode);
  }

  useEffect(() => {
    // F135: `app:apple` is its own page and hosts no wizard, so opening it no
    // longer asks the wizard shell to focus a step.
    const onPopState = () => restoreClientTarget(clientOpenTarget(window.location.search));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [initialClientTarget]);

  // Test mode: free movement between steps for visual tuning. Available only
  // outside production environments; activated via ?test=1, remembered per tab.
  const testAvailable = props.data.environment === "dev" || props.data.environment === "local";
  const [testMode, setTestMode] = useState<boolean>(() => {
    const param = new URLSearchParams(window.location.search).get("test");
    try {
      if (param === "1") { window.sessionStorage.setItem("launchpad-test-mode", "1"); return true; }
      if (param === "0") { window.sessionStorage.removeItem("launchpad-test-mode"); return false; }
      return window.sessionStorage.getItem("launchpad-test-mode") === "1";
    } catch { return param === "1"; }
  });
  const [testFocus, setTestFocus] = useState<RailStepKey | null>(null);
  const testActive = testMode && testAvailable;

  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("test")) return;
    url.searchParams.delete("test");
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);

  // F115 (audit finding 9): every deep link this app writes cleans itself up,
  // and a value the app does not know reads as one it merely has not opened
  // yet. `?open=neexistuje` landed on the overview and LEFT the parameter in
  // the address, so a reload, a bookmark or a shared link kept carrying it.
  // A valid target stays: it is what restores the surface on back and forward.
  useEffect(() => {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get("open");
    if (raw === null) return;
    // `clientOpenTarget` answers the raw value back when it knows it, so
    // "the app knows this link" is exactly `target === raw`. `?open=home` is
    // stripped too: home is the address with no parameter at all.
    const target = clientOpenTarget(url.search);
    if (target !== "home" && target === raw) return;
    url.searchParams.delete("open");
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);

  function testOpen(key: RailStepKey) {
    setTestFocus(key);
    setActiveQuestionnaire(null);
    const target = railTargetForStep(key);
    if (target === "q1" || target === "q2") {
      setSurfaceState("home");
      writeClientLocation(target, "push");
    } else if (target === "app:questionnaire") {
      requestWizardFocus("basics");
      setSurface(target);
    } else setSurface(target);
  }

  function openQuestionnaire(key: "q1" | "q2") {
    setSurfaceState("home");
    setActiveQuestionnaire(key);
    writeClientLocation(key, "push");
  }

  function testExit() {
    try { window.sessionStorage.removeItem("launchpad-test-mode"); } catch { /* soukromý režim */ }
    setTestMode(false);
    setTestFocus(null);
    setActiveQuestionnaire(null);
    setSurface("home");
  }

  function closeWelcome() {
    try { window.localStorage.setItem(welcomeKey, "1"); } catch { /* soukromý režim */ }
    setWelcomeOpen(false);
  }

  useEffect(() => {
    if (!unlockReveal) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("unlocked");
    url.searchParams.delete("status");
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  }, [unlockReveal]);
  // Deep links and rail clicks stay in ?open= so reload/back/forward restore
  // the exact client surface instead of deriving a fresh home default.
  const [resourceRefreshKey, setResourceRefreshKey] = useState(0);
  const openInboxSurface = (target: InboxSurface) => {
    pendingInboxFocusRef.current = target;
    setInboxFocusRequest((request) => request + 1);
    setSurface(target);
    setResourceRefreshKey((value) => value + 1);
  };

  useEffect(() => {
    const target = pendingInboxFocusRef.current;
    const root = dashboardMainRef.current;
    if (!target || !root) return;
    const focusTarget = () => {
      if (!focusNotificationDestination(root, target)) return false;
      pendingInboxFocusRef.current = null;
      return true;
    };
    if (focusTarget()) return;
    const observer = new MutationObserver(() => {
      if (focusTarget()) observer.disconnect();
    });
    observer.observe(root, { childList: true, subtree: true });
    const timeout = window.setTimeout(() => {
      observer.disconnect();
      if (pendingInboxFocusRef.current === target) pendingInboxFocusRef.current = null;
    }, 5000);
    return () => {
      observer.disconnect();
      window.clearTimeout(timeout);
    };
  }, [activeClientTarget, activeQuestionnaire, home.kind, inboxFocusRequest, surface]);
  useLayoutEffect(() => {
    resetSurfaceScroll();
  }, [surface]);
  useLayoutEffect(() => {
    if (activeQuestionnaire) resetSurfaceScroll();
  }, [activeQuestionnaire]);

  const loadHome = useCallback(async () => {
    if (!props.data.activeClientId) return;
    // Refreshes after an action swap the data in place; the full-page loading
    // screen only ever shows before the FIRST load (Pavel: no reload flashes).
    setHome((current) => current.kind === "ready" ? current : { kind: "loading" });
    try {
      const [homeResponse, deliveryResponse] = await Promise.all([
        fetch("/api/home", { credentials: "include" }),
        fetch("/api/deliverables", { credentials: "include" }).catch(() => null)
      ]);
      if (!homeResponse.ok) throw new Error("home_failed");
      const delivery = deliveryResponse?.ok
        ? await deliveryResponse.json() as DeliverablesResponse
        : { ok: true, deliverables: [], salespage: { locked: true, netlify: null } } satisfies DeliverablesResponse;
      setHome({
        kind: "ready",
        data: (await homeResponse.json()) as HomeResponse,
        delivery
      });
    } catch {
      setHome((current) => current.kind === "ready" ? current : { kind: "error" });
    }
  }, [props.data.activeClientId]);

  useEffect(() => {
    void loadHome();
  }, [loadHome]);

  // Clients come back to a stale tab from email; refreshing on focus keeps
  // the shown step truthful without any visible reload (data swaps in place).
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) void loadHome(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [loadHome]);

  // F39: a push while the tab is open opens the step in the rail on the
  // spot. The service worker posts the nudge; the data swaps in place, so
  // the client never reloads anything (Pavel: "BEZ aktualizace stránky").
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      if ((event.data as { type?: string } | null)?.type === "launchpad-notification") void loadHome();
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [loadHome]);

  // F92: no build polling here. The client no longer receives production
  // progress (the partner's approval stands between the build and the client),
  // so there is no moving axis to refresh; the notification e-mail and the
  // ordinary home loads carry the state change.

  // F39: on the first visit remember which steps are already open, so the
  // rail starts calm and only LATER openings pulse.
  useEffect(() => {
    const clientId = props.data.activeClientId;
    if (home.kind !== "ready" || !clientId) return;
    setSeenUnlocked((current) =>
      current ?? ensureSeenBaseline(clientId, buildRailSteps(home.data, home.delivery))
    );
  }, [home, props.data.activeClientId]);

  // A different client keeps its own ledger.
  useEffect(() => {
    setSeenUnlocked(null);
  }, [props.data.activeClientId]);

  // A submitted questionnaire hands over to the rail (F47): the handoff is set
  // only AFTER the home refresh, so the effect below reads the step states the
  // submission itself produced, not the ones from before it.
  const [submitHandoff, setSubmitHandoff] = useState<{ step: RailStepKey; transcriptsPending: boolean } | null>(null);
  const [celebration, setCelebration] = useState<
    { finished: RailStepKey; hold: "transcripts" | "queue" | null }
  | null>(null);
  const dismissCelebration = useCallback(() => setCelebration(null), []);

  // ONE view snapshot for the whole client surface, built ABOVE the early
  // returns so the handoff effect below can be an ordinary hook. Test mode is
  // one immutable client snapshot: the rail and every opened surface read this
  // same data instead of simulating their states separately.
  const clientView = useMemo(() => {
    if (home.kind !== "ready") return null;
    const testSnapshot = testActive && testFocus
      ? simulateClientSnapshot(home.data, home.delivery, testFocus)
      : null;
    const homeData = testSnapshot?.home ?? home.data;
    const viewDelivery = testSnapshot?.delivery ?? home.delivery;
    return {
      testSnapshot,
      homeData,
      viewDelivery,
      viewRailSteps: testSnapshot
        ? simulateRailSteps(buildRailSteps(homeData, viewDelivery), testFocus, journeyAxesOf(homeData.client))
        : buildRailSteps(homeData, viewDelivery)
    };
  }, [home, testActive, testFocus]);

  // 🔴 fix-homereview scope 2 (Pavel 2026-08-19, his THIRD time, verbatim):
  // "Žádná domovská stránka klienta nemá existovat. Má ho to dávat na tu
  // stránku, kterou má odemčenou a se kterou má pracovat." A load with NO
  // explicit destination therefore lands on the CURRENT step's own page
  // (defaultLandingTarget, src/landing.ts - the rail's own primaryNext
  // derivation, never a second one). Explicit destinations keep winning:
  // a ?open= deep link (mail CTAs land as those, F140), the unlock reveal,
  // the test walkthrough. Decided ONCE per app load, so the focus and
  // notification refreshes never yank the client off a page they chose; and
  // NOTHING is written to the address bar, so tomorrow's reload lands on
  // tomorrow's current step instead of a bookmarked yesterday (rail clicks
  // keep writing ?open= - that is explicit navigation, this is not).
  const autoLanded = useRef(false);
  useEffect(() => {
    if (autoLanded.current || !clientView) return;
    autoLanded.current = true;
    if (initialClientTarget !== "home" || unlockReveal || testActive) return;
    const target = defaultLandingTarget(clientView.homeData, clientView.viewRailSteps);
    if (target) restoreClientTarget(target);
  }, [clientView, unlockReveal, testActive]);

  // The submitted questionnaire moves the client to the first step that is
  // actually open. Transcripts still running = that step is the questionnaire
  // itself, so we stay put and say why instead of jumping nowhere (F47).
  // This effect MUST stay above the early returns further down. React counts
  // hooks per render: placed below `if (home.kind === "loading") return ...`
  // it runs only on the loaded render, which is error #310 and takes the whole
  // client surface down with it (caught by looking at the page, 2026-08-05).
  useEffect(() => {
    if (!submitHandoff || !clientView) return;
    const next = currentIncompleteRailStep(clientView.viewRailSteps);
    const moves = Boolean(next && next.key !== submitHandoff.step);
    // Three honest outcomes, never one guess: we move the client on, or the
    // transcripts still hold them here, or nothing is open yet and we say that
    // instead of blaming voice notes that are already written up.
    setCelebration({
      finished: submitHandoff.step,
      hold: moves ? null : submitHandoff.transcriptsPending ? "transcripts" : "queue"
    });
    if (next && moves) setSurface(next.target);
    setSubmitHandoff(null);
  }, [submitHandoff, clientView]);

  // Client switch returns to home, but the very first run must not clobber
  // the ?open=<section> deep link the session was entered with.
  const surfaceResetReady = useRef(false);
  useEffect(() => {
    if (!surfaceResetReady.current) { surfaceResetReady.current = true; return; }
    setSurface("home", "replace");
  }, [props.data.activeClientId]);

  async function logout() {
    setActionError(null);
    const response = await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "include",
      headers: { "X-Launchpad-CSRF": props.data.csrfToken }
    });
    if (response.ok) {
      // Tabsync: the cookie just changed hands (or died) for the whole
      // browser - the other tabs re-check /me and follow it.
      announceIdentityChange();
      if (props.data.impersonation) {
        window.location.assign("/");
        return;
      }
      await clearQuestionnaireDrafts(`${props.data.user.id}:${props.data.activeClientId ?? "none"}`);
      props.onLoggedOut();
    }
    else setActionError(await actionIssueFrom(response));
  }

  async function switchClient(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setActionError(null);
    setSwitching(true);
    const response = await fetch("/api/auth/switch-client", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-Launchpad-CSRF": props.data.csrfToken },
      body: JSON.stringify({ clientId: switchTarget })
    });
    if (response.ok) {
      // Tabsync: switch-client ROTATES the session, so tabs still rendering
      // the previous client hold a cookie that now belongs to another one.
      announceIdentityChange();
      await props.onReload();
    }
    else setActionError(await actionIssueFrom(response));
    setSwitching(false);
  }

  // F81: the video's continue button. The server already opened the next
  // step at the unlock moment; this refreshes the journey and leads the
  // client straight into the questionnaire the video was preparing them for.
  async function continueFromVideo(stepKey: VideoStepKey) {
    await loadHome();
    openQuestionnaire(stepKey === "marketing_call" ? "q2" : "q1");
  }

  if (props.data.user.staffRole === "partner" || props.data.user.staffRole === "admin") {
    return (
      <StaffConsole
        role={props.data.user.staffRole}
        csrfToken={props.data.csrfToken}
        locale={props.locale}
        onLocaleChange={props.onLocaleChange}
        t={props.t}
        onLogout={logout}
        email={props.data.user.email}
        displayName={props.data.user.displayName}
        currentUserId={props.data.user.id}
      />
    );
  }

  if (!props.data.activeClientId) {
    return (
      <main className="staff-state">
        <img className="brand-logo" src="/assets/buddypro-logo.png" alt={props.t("common.brandAlt")} />
        <p className="eyebrow blue-eyebrow">{props.t("staff.eyebrow")}</p>
        <h1>{props.t("staff.title")}</h1>
        <p className="lead">{props.t("staff.description")}</p>
        <button className="text-button compact-button" type="button" onClick={() => void logout()}>{props.t("common.logout")}</button>
      </main>
    );
  }

  if (home.kind === "loading") return <LoadingScreen t={props.t} />;
  if (home.kind === "error") return <ErrorScreen t={props.t} retry={loadHome} />;

  // Same snapshot the handoff effect above reads; it exists whenever home is
  // ready, so this guard only satisfies the type narrowing.
  if (!clientView) return <LoadingScreen t={props.t} />;
  const { testSnapshot, homeData, viewDelivery, viewRailSteps } = clientView;
  const viewSections = homeData.sections;
  const clientT = (key: string) => clientStepTranslation(key, viewRailSteps, props.t);
  const currentRailStep = viewRailSteps.find((step) => step.primaryNext) ?? null;
  // The card names the current step. `primaryNext` is the server flag; when a
  // step and its section are BOTH `next` the ball is demonstrably with the
  // client, so a stale flag must not turn the overview into "Teď pracuje tým"
  // behind an open step (F48). Deliverable steps keep the flag requirement -
  // their section is `working` while the team produces the material.
  const primary = currentRailStep
    ? viewSections.find((section) => section.key === currentRailStep.backingSection
        && (section.primaryNext || (currentRailStep.state === "next" && section.state === "next"))) ?? null
    : null;
  const primaryIsCall = currentRailStep?.key === "onboarding_call" || currentRailStep?.key === "marketing_call";
  const sectionState = (key: string) => viewSections.find((section) => section.key === key)?.state ?? "locked";
  const callRelevant = ["next", "working"].includes(sectionState("onboarding_call"))
    || ["next", "working"].includes(sectionState("marketing_call"));
  // Early phase = before the first questionnaire is done; the guided steps
  // and the profile card live here. Later phases keep the home minimal.
  const earlyPhase = sectionState("q1") !== "done";
  // Phases = rail steps (Pavel). The home header names the current phase, and
  // the questionnaire phases render their questions right on the page.
  // 🔴 F152-W4 (Pavel 2026-08-14): "Schválený webinář = konec cesty." One
  // derivation for both journeys, in src/journey-end.ts with its reasons.
  const journeyFinished = journeyEnded(homeData);
  // Headline, kicker and the finished card are ONE state (src/journey-end.ts):
  // while a section still carries the client's turn nothing congratulates.
  const showsFinished = homeShowsFinished(journeyFinished, Boolean(primary));
  const inlineQuestionnaire = null as "q1" | "q2" | null;
  const openedRailStep = activeQuestionnaire
    ? viewRailSteps.find((step) => step.key === activeQuestionnaire) ?? null
    : surface === "home" || surface === "account"
      ? null
      : viewRailSteps.find((step) =>
          step.target === surface || (step.target === "app:apple" && surface === "app")
        ) ?? null;
  // ONE gate: rail click, deep link and notification "Otevřít" all land here.
  // Know-how used to slip through because the team's `working` window is not
  // `locked` - the shared-folder notification opened the whole workspace
  // before the questionnaire was even submitted.
  const lockedStep = openedRailStep && clientStepLocked(openedRailStep) ? openedRailStep : null;
  // 🔴 F152-W2 (W1's open point): a deep link can name a step this client's
  // journey does not contain - `?open=onboarding_call` on a sales_only client
  // is the case W1 left standing after it withheld the VIDEO on the server.
  // Without this the surface fell through to the step's own page, which is the
  // one thing the F92 law forbids: a page for a step that is not theirs. It is
  // not `lockedStep` either, and the difference is the whole sentence - a
  // padlock says "later", and this step will never come.
  //
  // 🔴 Two exclusions, both load-bearing, both found by reading what a
  // notification can really link to (shared/notification-targets.ts):
  //
  // 1. `railStepEverDrawn` keeps `stripe` and the publication out. They are
  //    hidden from EVERY client's rail (F82) and their pages are still live -
  //    the publication is phase 3 of the sales page step, and a section
  //    notification can name the stripe section. Refusing them would tell a
  //    client that a step they really have is not part of their journey.
  // 2. The EXACT target only, never the `app:apple` -> `app` alias the lock
  //    lookup above uses. A client who did not buy the app can still open the
  //    app surface to say they are interested (`/api/app/interest`), so that
  //    surface is theirs; the Apple step's own page is not.
  const offJourneyStepKey = openedRailStep
    ? null
    : activeQuestionnaire
      // `?open=q1` on a sales_only client. The questionnaire opens on its own
      // surface (`dashboardSurfaceForTarget` sends q1/q2 to `home`), so the
      // lookup below never sees it - and the runner would have shown them an
      // empty finished questionnaire about a Buddy they had built elsewhere.
      // Same fiction, same refusal.
      ? activeQuestionnaire
      : surface !== "home" && surface !== "account"
        ? RAIL_STEP_KEYS.find((key) => railStepEverDrawn(key) && railTargetForStep(key) === surface) ?? null
        : null;
  const refusedStepKey = lockedStep?.key ?? offJourneyStepKey;
  const lockedStepCopyKey = offJourneyStepKey
    ? "rail.notInJourney"
    : lockedStep?.state === "working"
      ? `rail.unlockPreparing.${lockedStep.key}`
      : undefined;
  const activeRailStep = openedRailStep ?? currentRailStep;
  const incompleteRailStep = currentIncompleteRailStep(viewRailSteps);
  // K05: a padlock reads as "later" only when the client can see where it sits
  // and what to do instead. Both facts already exist on this page; the locked
  // surface simply never got them, so it was the one dead end in the product.
  const lockedStepIndex = lockedStep ? viewRailSteps.findIndex((step) => step.key === lockedStep.key) : -1;
  const lockedStepPosition = lockedStepIndex >= 0
    ? { current: lockedStepIndex + 1, total: viewRailSteps.length }
    : undefined;
  const lockedStepNext = refusedStepKey && incompleteRailStep && incompleteRailStep.key !== refusedStepKey
    ? { target: incompleteRailStep.target, title: props.t(STEP_HEADING_KEYS[incompleteRailStep.key]) }
    : undefined;
  // 🔴 F147: the SERVER can refuse a questionnaire the rail believes is open -
  // that drift is exactly what Pavel walked into - and the runner's own refusal
  // page used to be a dead end. It gets the same pair `LockedStep` already
  // takes: where the client really is now, and a button to it. Computed here,
  // because the rail lives here; never re-derived inside the runner.
  const questionnaireLockedNext = (key: "q1" | "q2") => (
    incompleteRailStep && incompleteRailStep.key !== key
      ? { target: incompleteRailStep.target, title: props.t(STEP_HEADING_KEYS[incompleteRailStep.key]) }
      : null
  );
  // One card, two jobs. It is the PAGE where nothing else draws a heading (the
  // four steps whose surface renders null, and the questionnaire, which steps
  // aside for it); everywhere else it stands over a step page that draws its
  // own h1, so it renders as a BANNER and the page keeps one page title
  // (fix3-nedodelky, client N02: salespage, strategy and handover each had two).
  // F134b: the feedback step closes the moment the instance changes hands, so
  // its card must name THAT reason. The fact travels on the home payload (the
  // same one the podnět route refuses on), never re-derived here.
  const closedStepBenefitKey = openedRailStep?.key === "feedback" && homeData.ownershipTransferred
    ? "closedStep.steps.feedback.ownershipBenefit"
    : undefined;
  const renderClosedStep = (variant: "page" | "banner") => openedRailStep?.state === "done" ? (
    <ClosedStep
      step={openedRailStep.key}
      nextTarget={incompleteRailStep?.target ?? null}
      nextStepTitle={incompleteRailStep ? props.t(STEP_HEADING_KEYS[incompleteRailStep.key]) : null}
      variant={variant}
      benefitKey={closedStepBenefitKey}
      t={props.t}
      onContinue={(target) => setSurface(target)}
    />
  ) : null;
  const closedStep = renderClosedStep("page");
  const closedStepBanner = renderClosedStep("banner");
  const closedCardOnly = openedRailStep?.state === "done"
    && ["onboarding_call", "marketing_call", "knowhow", "training"].includes(openedRailStep.key);
  // F35: the call rail step opens the overview's own call panel, never a
  // second, poorer screen.
  const isCallSurface = surface === "onboarding_call" || surface === "marketing_call";
  const callSurfaceKey: VideoStepKey = surface === "marketing_call" ? "marketing_call" : "onboarding_call";
  // F143: "před hovorem" means the ÚVODNÍ video, and the two steps it opens
  // (Profil, Apple účet) are chapter 1. A sales_only client has no chapter 1,
  // so this block is not "empty" for them - it is about a part of the journey
  // they were never sold.
  const salesOnlyJourney = homeData.client.journeyMode === "sales_only";
  const preCallStepsOpen = !salesOnlyJourney
    && (homeData.profileConfirmed !== true || homeData.client.includesApp);
  const phaseNumber = currentRailStep ? viewRailSteps.findIndex((step) => step.key === currentRailStep.key) + 1 : 0;
  const currentRailTitle = currentRailStep
    ? props.t(STEP_HEADING_KEYS[currentRailStep.key])
    : props.t("home.waiting");
  const mobileRailStep = currentRailStep
    ?? [...viewRailSteps].reverse().find((step) => step.state !== "locked")
    ?? viewRailSteps[0]
    ?? null;
  // F39: steps that opened since the client last looked pulse in the rail
  // until they click them. Test mode simulates states, so it never pulses.
  const freshUnlocked = testActive ? [] : freshUnlockedKeys(seenUnlocked, viewRailSteps);
  const markStepSeen = (key: string) => {
    setSeenUnlocked((current) => {
      const next = acknowledgeStep(current ?? [], key);
      if (props.data.activeClientId) writeSeenUnlocked(props.data.activeClientId, next);
      return next;
    });
  };
  const advanceTest = async () => {
    const next = nextRailStep(viewRailSteps, testFocus ?? currentRailStep?.key ?? null);
    if (next) testOpen(next.key);
  };

  return (
    <main className={`dashboard-shell${testActive ? " test-mode-active" : ""}`}>
      {/* N05: the page is DRAWN header-first (the bell sits at top = 37 px)
          and READ header-last: the bell and the account were the 142nd and
          143rd of 145 tab stops on the sales questionnaire, because on the
          desktop layout they live in the trailing context column. Moving them
          in the DOM would move them on screen between 768 and 1100 px, where
          that column really does sit below the content; two skip links at the
          very top fix the reading order without moving a single pixel, and
          the first one also answers the 48-stop rail. Fixed, so it is not a
          grid item: zero layout effect until it takes focus. */}
      <nav className="skip-links" aria-label={props.t("dashboard.skipLabel")}>
        <a className="skip-link" href="#dashboard-main">{props.t("dashboard.skipToContent")}</a>
        {!mobileLayout && (
          <a className="skip-link" href="#account-tools">{props.t("dashboard.skipToAccount")}</a>
        )}
      </nav>
      {testActive && (
        <TestBar
          steps={viewRailSteps}
          focus={testFocus}
          t={props.t}
          onOpen={testOpen}
          onExit={testExit}
        />
      )}
      {welcomeOpen && !props.data.impersonation && !testActive && (
        <WelcomeGuide
          t={props.t}
          role={homeData.client.membershipRole}
          currentStep={currentRailStep}
          returning={viewRailSteps.some((step) => step.state === "done")}
          onDone={closeWelcome}
        />
      )}
      <aside className="progress-panel" aria-label={props.t("dashboard.processLabel")}>
        {mobileLayout ? (
          <div className="mobile-client-header">
            <button className="dashboard-home-button" type="button" onClick={() => setSurface("home")} aria-label={props.t("home.openHome")}>
              <img className="brand-logo dashboard-logo" src="/assets/buddypro-logo.png" alt={props.t("common.brandAlt")} />
            </button>
            <MobileStepSwitcher
              steps={viewRailSteps}
              currentStepKey={activeRailStep?.key ?? mobileRailStep?.key ?? null}
              profilePhotoUrl={homeData.profilePhotoUrl}
              locale={props.locale}
              t={props.t}
              onSurfaceOpen={setSurface}
              onQuestionnaireOpen={openQuestionnaire}
              testMode={testActive}
              onTestOpen={testOpen}
              freshUnlocked={freshUnlocked}
              onStepSeen={markStepSeen}
            />
            <NotificationCenter
              activeClientId={props.data.activeClientId}
              csrfToken={props.data.csrfToken}
              locale={props.locale}
              t={props.t}
              onConfirmed={loadHome}
              onNavigate={openInboxSurface}
              onOpened={loadHome}
              onLiveUpdate={loadHome}
              activeSurface={activeClientTarget}
            />
            <button
              className="mobile-account-link"
              type="button"
              title={props.data.user.email}
              aria-label={props.t("account.link")}
              onClick={() => setSurface("account")}
            >
              <span className="account-avatar" aria-hidden="true">{props.data.user.email.slice(0, 1).toUpperCase()}</span>
            </button>
          </div>
        ) : (
          <>
            <button className="dashboard-home-button" type="button" onClick={() => setSurface("home")} aria-label={props.t("home.openHome")}>
              <img className="brand-logo dashboard-logo" src="/assets/buddypro-logo.png" alt={props.t("common.brandAlt")} />
            </button>
            {/* The chapter labels (Priprava Buddyho / prodeje / appka) carry the
                structure now - a "Prubeh" heading above them doubled up. */}
            <div className="progress-desktop">
              {/* 🔴 fix-homereview scope 2: the row tint marks the OPEN page
                  and nothing else. It used to fall back to the journey's
                  current step, so on the home the rail highlighted "Sales
                  strategy" beside content that was not that step (Pavel's
                  screenshot). With the default landing the two agree by
                  construction on every step page; on the residual home
                  nothing is tinted - the current step still carries its own
                  state ("Na řadě", the blue unlocked marker). The MOBILE
                  switcher above keeps the fallback: its pill is a position
                  and navigation control ("Krok X z Y" opens the map), not a
                  selection. */}
              <ProgressRail steps={viewRailSteps} currentStepKey={openedRailStep?.key ?? null} profilePhotoUrl={homeData.profilePhotoUrl} locale={props.locale} t={props.t} onSurfaceOpen={setSurface} onQuestionnaireOpen={openQuestionnaire} testMode={testActive} onTestOpen={testOpen} freshUnlocked={freshUnlocked} onStepSeen={markStepSeen} />
            </div>
          </>
        )}
      </aside>

      <section className="dashboard-main" id="dashboard-main" tabIndex={-1} aria-labelledby="dashboard-title" ref={dashboardMainRef}>
        {celebration && (
          <StepCelebration
            finished={celebration.finished}
            hold={celebration.hold}
            nextStepTitle={incompleteRailStep ? props.t(STEP_HEADING_KEYS[incompleteRailStep.key]) : null}
            t={props.t}
            onDismiss={dismissCelebration}
          />
        )}
        <ErrorBoundary variant="inline" t={props.t} resetKey={`${surface}:${activeQuestionnaire ?? "home"}`}>
        {refusedStepKey ? (
          <LockedStep
            step={refusedStepKey}
            copyKey={lockedStepCopyKey}
            t={props.t}
            onBack={() => setSurface("home")}
            position={lockedStepPosition}
            next={lockedStepNext}
            // A step outside the journey has no "co tě čeká": the preview would
            // sell three bullet points about work this client never bought.
            preview={!offJourneyStepKey}
            onContinue={(target) => { setSurface(target); resetSurfaceScroll(); }}
          />
        ) : activeQuestionnaire ? (
          <QuestionnaireRunner
            questionnaire={activeQuestionnaire}
            csrfToken={props.data.csrfToken}
            locale={props.locale}
            draftNamespace={`${props.data.user.id}:${props.data.activeClientId}`}
            testMode={testActive}
            position={railStepPosition(viewRailSteps, activeQuestionnaire)}
            next={questionnaireLockedNext(activeQuestionnaire)}
            onOpenStep={(target) => { setSurface(target); resetSurfaceScroll(); }}
            t={props.t}
            closedStep={closedStep}
            onClose={() => setSurface("home")}
            onOpenProfile={() => {
              setSurface("profile");
              resetSurfaceScroll();
            }}
            onComplete={loadHome}
            onSubmitted={async (target, info) => {
              if (target === "next") {
                // Refresh FIRST: the rail decides where to go, and it can only
                // decide from the states the submission just wrote.
                await loadHome();
                setSubmitHandoff({ step: activeQuestionnaire, transcriptsPending: info.transcriptsPending });
                return;
              }
              setSurface(target);
              resetSurfaceScroll();
              await loadHome();
            }}
            onTestAdvance={advanceTest}
          />
        ) : surface === "home" ? (
          <div data-client-home="overview">
            {/* 🔴 F152-W4: a finished journey has no current step. The full
                journey never met this - when its every section is done no rail
                step is `next`/`working`, so `primaryRailKey` finds nothing and
                the counter is already 0. A sales_only journey ends on the four
                MATERIALS, and the sales page keeps its own phases 2-3 (order
                links, publication - F120/F137), so the pointer can still name a
                step: without this guard the page read "Krok 5 z 7" over the
                headline "Prodejní balíček je hotový". */}
            {/* 🔴 fix-homereview scope 2: the home never wears a step counter.
                Pavel's screenshot had "KROK 4 Z 7" over the home title while
                the card below talked about preparing - three surfaces
                disagreeing on one screen ("Pokud je tam nějaká domovská
                stránka, tak rozhodně nemá říkat to, co říká"). The step's
                number belongs to the step's page (F147's law, finished for
                the eyebrow too). Test mode keeps the counter: it is the dev
                walkthrough's own label under the amber bar, the same
                exception F147 wrote for the borrowed title. */}
            <p className="eyebrow blue-eyebrow">{testActive && phaseNumber > 0 && !showsFinished
              ? props.t("phase.progress").replace("{current}", String(phaseNumber)).replace("{total}", String(viewRailSteps.length))
              : props.t("home.eyebrow")}</p>
            {/* Each phase gets its own headline so the journey reads as a
                story, not one static claim (design pass wave 4). */}
            {/* 🔴 F147: the home is the HOME. It used to borrow the current
                step's name whenever no section carried the pointer, so the
                client landed on a page titled "Buddy brief / KROK 4 Z 17"
                that was not the Buddy brief - it was the overview, with its
                own next-action card, its own "Toto můžeš udělat mezitím" and
                its own completed-steps fold. Pavel read that as a second,
                broken copy of the step ("síť alternativních stránek, které
                nejsou funkční"). One step, one page (F135): the step's name
                belongs to the step's page, and the rail is what says where
                the client is. Test mode keeps the borrowed title - it is the
                dev walkthrough's own label, and it renders under a loud
                amber bar that never passes for the product. */}
            <h1 id="dashboard-title">{testActive && currentRailStep
              ? currentRailTitle
              : inlineQuestionnaire
              ? props.t(`sections.${inlineQuestionnaire}.title`)
              : primary && PHASE_TITLE_KEYS.has(primary.key)
                ? props.t(`home.phaseTitles.${primary.key}`)
                : showsFinished
                  ? props.t(salesOnlyJourney ? "home.finished.pageTitleSalesOnly" : "home.finished.pageTitle")
                  : props.t("home.title")}</h1>
            {/* The client's name reads as a greeting on the home overview;
                inside the inline questionnaire it was just noise. */}
            {!inlineQuestionnaire && <p className="lead">{homeData.client.name}</p>}

            {unlockReveal?.already && (
              <div className="unlock-reveal" role="status" data-unlock-reveal={unlockReveal.section}>
                <strong>{props.t("home.unlockedTitle")}</strong>
                <p>{props.t("home.unlockedAlready")}</p>
              </div>
            )}

            {primary?.key !== "onboarding_call" && !inlineQuestionnaire && (
            <PrimaryNextAction
              section={primary}
              workingSection={viewSections.find((s) => s.state === "working" && s.key !== "app")?.key ?? null}
              workingStep={currentRailStep}
              railSteps={viewRailSteps}
              delivery={viewDelivery}
              finished={journeyFinished}
              journeyMode={homeData.client.journeyMode}
              existingBuddy={homeData.client.instanceStartMode === "existing"}
              t={props.t}
              onQuestionnaireOpen={openQuestionnaire}
              onSurfaceOpen={setSurface}
              profilePhotoUrl={homeData.profilePhotoUrl}
              locale={props.locale}
              video={primary?.key === "marketing_call" ? homeData.videos.marketing : homeData.videos.onboarding}
              csrfToken={props.data.csrfToken}
              onVideoContinue={continueFromVideo}
            />
            )}

            {callRelevant && !primaryIsCall && !earlyPhase && (
            <section className="home-steps home-grid" aria-label={props.t("home.overviewLabel")}>
              <ol className="home-steps-list">
                {/* F35: the same panel as everywhere - one video step, one
                    shape (F81: the step is the gated video, no call dates). */}
                <VideoStepPanel
                  stepKey={sectionState("onboarding_call") !== "done" ? "onboarding_call" : "marketing_call"}
                  number={railStepPosition(
                    viewRailSteps,
                    sectionState("onboarding_call") !== "done" ? "onboarding_call" : "marketing_call"
                  )?.current ?? 1}
                  csrfToken={props.data.csrfToken}
                  t={props.t}
                  video={sectionState("onboarding_call") !== "done" ? homeData.videos.onboarding : homeData.videos.marketing}
                  onContinue={() => continueFromVideo(sectionState("onboarding_call") !== "done" ? "onboarding_call" : "marketing_call")}
                />
              </ol>
            </section>
            )}

            {inlineQuestionnaire && (
              <div className="home-questionnaire">
                <QuestionnaireRunner
                  questionnaire={inlineQuestionnaire}
                  csrfToken={props.data.csrfToken}
                  locale={props.locale}
                  draftNamespace={`${props.data.user.id}:${props.data.activeClientId}`}
                  testMode={testActive}
                  t={props.t}
                  closedStep={closedStep}
                  onClose={() => loadHome()}
                  onOpenProfile={() => {
                    setSurface("profile");
                    resetSurfaceScroll();
                  }}
                  onComplete={loadHome}
                  onSubmitted={async (target, info) => {
                    if (target === "next") {
                      await loadHome();
                      setSubmitHandoff({ step: inlineQuestionnaire, transcriptsPending: info.transcriptsPending });
                      return;
                    }
                    setSurface(target);
                    resetSurfaceScroll();
                    await loadHome();
                  }}
                  onTestAdvance={advanceTest}
                />
              </div>
            )}

            {/* F141 (Pavel 2026-08-13) DELETED the step list that stood here.
                F131 put it in because one "co delat ted" card lied to a client
                with several open steps; the RAIL answers that, and it is 200 px
                to the left with the same states and the same click-through.
                "Ty kroky ja vidim vlevo a nepotrebuju ten seznam vsech tvych
                kroku uprostred." So the middle column carries ACTION cards
                only - the video, the profile, and the app track when the client
                bought one. */}
            {earlyPhase && !inlineQuestionnaire && (
              <HomeSteps
                includesApp={homeData.client.includesApp}
                canInvite={homeData.invites.canManage}
                csrfToken={props.data.csrfToken}
                locale={props.locale}
                t={props.t}
                onOpenProfile={() => setSurface("profile")}
                onOpenApp={() => setSurface("app")}
                video={homeData.videos.onboarding}
                onVideoContinue={() => continueFromVideo("onboarding_call")}
                profileConfirmed={homeData.profileConfirmed === true}
                currentStep={currentRailStep}
                onboardingStep={viewRailSteps.find((step) => step.key === "onboarding_call") ?? null}
              />
            )}
          </div>
        ) : (
          <>
            {/* The call step needs no way "back to the overview": it IS the
                overview's card and the rail sits next to it (Pavel F35). */}
            {!isCallSurface && (
              <button className="surface-back" type="button" onClick={() => setSurface("home")}>{props.t("common.backHome")}</button>
            )}
            {closedCardOnly ? closedStep : closedStepBanner}
            {isCallSurface ? (
              closedCardOnly ? null : <section className="m4-view call-focus-view" aria-labelledby="call-focus-title">
                <header className="m4-heading">
                  <p className="eyebrow blue-eyebrow">{(() => {
                    const position = railStepPosition(viewRailSteps, surface);
                    return position
                      ? props.t("phase.progress").replace("{current}", String(position.current)).replace("{total}", String(position.total))
                      : props.t("home.eyebrow");
                  })()}</p>
                  <h1 id="call-focus-title">{props.t(STEP_HEADING_KEYS[surface])}</h1>
                </header>
                {/* The SAME panel the overview shows (F35): one video step,
                    one shape - on the rail surface and on home alike. */}
                <section className="home-steps" aria-label={props.t("dashboard.processLabel")}>
                  <ol className="home-steps-list">
                    <VideoStepPanel
                      stepKey={callSurfaceKey}
                      number={railStepPosition(viewRailSteps, surface)?.current ?? 1}
                      csrfToken={props.data.csrfToken}
                      t={props.t}
                      video={callSurfaceKey === "marketing_call" ? homeData.videos.marketing : homeData.videos.onboarding}
                      onContinue={() => continueFromVideo(callSurfaceKey)}
                    />
                  </ol>
                  {/* "Co můžeš udělat před hovorem" travels with the call
                      step, so opening it from the rail loses nothing. */}
                  {callSurfaceKey === "onboarding_call" && preCallStepsOpen && (
                    <>
                      <h2 className="home-steps-subheading">{props.t("homeSteps.beforeCall")}</h2>
                      <PreCallSteps
                        profileConfirmed={homeData.profileConfirmed === true}
                        includesApp={homeData.client.includesApp}
                        t={props.t}
                        onOpenProfile={() => setSurface("profile")}
                        onOpenApp={() => setSurface("app")}
                      />
                    </>
                  )}
                </section>
              </section>
            ) : surface === "profile" ? (
              <div className={`${homeData.profileConfirmed === true ? "profile-view-confirmed profile-mobile-map-reader" : ""} ${profileHistoryOpen ? "profile-history-open" : "profile-history-collapsed"}`}>
                {homeData.profileConfirmed === true && (
                  <header className="confirmed-profile-intro">
                    <p className="eyebrow blue-eyebrow">{props.t("profile.confirmed.eyebrow")}</p>
                    <h1>{props.t("profile.confirmed.title")}</h1>
                    <p className="lead">{props.t("profile.confirmed.copy")}</p>
                    <div className="confirmed-profile-actions">
                      {/* F162: no mailto here either; the partner card carries
                          the in-app composer, so the link jumps to it. */}
                      {homeData.partner ? (
                        <a className="secondary-button compact-button" href="#partner-card">
                          {props.t("profile.confirmed.requestEdit")}
                        </a>
                      ) : <span className="profile-edit-unavailable">{props.t("profile.confirmed.requestEditUnavailable")}</span>}
                      <button className="quiet-link" type="button" aria-expanded={profileHistoryOpen} onClick={() => setProfileHistoryOpen((open) => !open)}>
                        {props.t(profileHistoryOpen ? "profile.history.hide" : "profile.history.show")}
                      </button>
                    </div>
                  </header>
                )}
                <ProfileView
                  csrfToken={props.data.csrfToken}
                  locale={props.locale}
                  clientName={homeData.client.name}
                  confirmed={homeData.profileConfirmed === true}
                  // 🔴 F152-W2 (audit drobnost 4): `clientT`, not the raw `t`.
                  // The profile is a rail STEP and its kicker has to say which
                  // one ("Krok 2 z 7" in the sales_only journey, "Krok 2 z 14"
                  // in the full one). `clientStepTranslation` rewrites exactly
                  // the keys in STEP_EYEBROW_RAIL_KEYS and passes every other
                  // key straight through, so this is the same function every
                  // other step page is already given.
                  // F145: an absent fact is never read as "the machine is on
                  // it". A payload from before this shipped degrades to the
                  // state that promises nothing and points at the microphone.
                  research={homeData.profileResearch ?? "none"}
                  profilePhotoUrl={homeData.profilePhotoUrl}
                  t={clientT}
                  onChanged={loadHome}
                />
              </div>
            ) : surface === "account" ? (
              <section className="m4-view account-view" aria-labelledby="account-title">
                <header className="m4-heading">
                  <p className="eyebrow blue-eyebrow">{props.t("account.eyebrow")}</p>
                  <h1 id="account-title">{props.t("account.title")}</h1>
                </header>
                <article className="account-card">
                  <p className="account-email"><span>{props.t("account.emailLabel")}</span><strong className="break-text">{props.data.user.email}</strong></p>
                  <button className="secondary-button compact-button" type="button" onClick={() => void logout()}>{props.t("common.logout")}</button>
                </article>
                <article className="account-card account-language-card">
                  <LanguagePreference
                    locale={props.locale}
                    csrfToken={props.data.csrfToken}
                    t={props.t}
                    onChanged={props.onLocaleChange}
                  />
                </article>
                {homeData.invites.canManage && (
                  <article className="account-card">
                    <InviteColleague csrfToken={props.data.csrfToken} t={props.t} />
                  </article>
                )}
                <NotificationSettings csrfToken={props.data.csrfToken} t={props.t} />
              </section>
            ) : isM5bSurface(surface) ? (
              <M5bSectionView
                surface={surface}
                sections={viewSections}
                csrfToken={props.data.csrfToken}
                locale={props.locale}
                includesApp={homeData.client.includesApp}
                // F143 (N10): the sales overview numbers its rows and picks
                // which rows exist from THIS client's rail, never from a
                // literal. `viewRailSteps` is what the rail beside it draws,
                // so the two can never disagree.
                railSteps={viewRailSteps}
                membershipRole={homeData.client.membershipRole}
                partner={homeData.partner}
                profilePhotoUrl={homeData.profilePhotoUrl}
                t={clientT}
                onChanged={loadHome}
                onNavigate={setSurface}
                testSnapshot={testSnapshot ?? undefined}
                testMode={testActive}
              />
            ) : (
              closedCardOnly ? null : <M4SectionView surface={surface} sections={viewSections} csrfToken={props.data.csrfToken} locale={props.locale} clientId={homeData.client.id} membershipRole={homeData.client.membershipRole} profilePhotoUrl={homeData.profilePhotoUrl} betaProgress={homeData.stepProgress?.beta ?? null} t={props.t} onChanged={loadHome} onNavigate={setSurface} testResources={testSnapshot?.resources} refreshKey={resourceRefreshKey} />
            )}
          </>
        )}

        </ErrorBoundary>
        {actionError && <p className="message error-message" role="alert">{props.t(actionIssueLocaleKey(actionError))}</p>}
      </section>

      <aside className="context-panel" aria-label={props.t("dashboard.contextLabel")}>
        {!mobileLayout && (
          <div className="account-tools" id="account-tools" tabIndex={-1}>
            <NotificationCenter
              activeClientId={props.data.activeClientId}
              csrfToken={props.data.csrfToken}
              locale={props.locale}
              t={props.t}
              onConfirmed={loadHome}
              onNavigate={openInboxSurface}
              onOpened={loadHome}
              onLiveUpdate={loadHome}
              activeSurface={activeClientTarget}
            />
            <button className="account-link" type="button" title={props.data.user.email} onClick={() => setSurface("account")}>
              <span className="account-avatar" aria-hidden="true">{props.data.user.email.slice(0, 1).toUpperCase()}</span>
              <span className="account-meta">
                <strong>{props.t("account.link")}</strong>
                <span className="break-text">{props.data.user.email}</span>
              </span>
              <span className="account-chevron" aria-hidden="true">›</span>
            </button>
          </div>
        )}
        <p className="panel-label">{props.t("partner.eyebrow")}</p>
        <PartnerControl partner={homeData.partner} t={props.t} csrfToken={props.data.csrfToken} replyEmail={props.data.user.email} />
        {props.data.memberships.length > 1 && (
          <form className="switch-form" onSubmit={switchClient}>
            <label htmlFor="client-switch">{props.t("dashboard.switchLabel")}</label>
            <select
              id="client-switch"
              value={switchTarget}
              onChange={(event) => setSwitchTarget(event.target.value)}
              disabled={switching}
            >
              {props.data.memberships.map((membership) => (
                <option key={membership.clientId} value={membership.clientId}>{membership.clientName}</option>
              ))}
            </select>
            <button className="secondary-button" type="submit" disabled={switching || switchTarget === props.data.activeClientId}>
              {switching ? props.t("dashboard.switching") : props.t("dashboard.switchSubmit")}
            </button>
          </form>
        )}
      </aside>
    </main>
  );
}

const SURFACE_SECTIONS = [
  "profile", "account",
  "knowhow", "training", "beta", "beta:feedback",
  "sales", "strategy", "salespage", "emails", "webinar", "publish",
  "stripe", "payments", "handover", "app", "app:apple", "app:questionnaire", "app:release"
] as const;
// Sections with their own home headline (design pass wave 4).
const PHASE_TITLE_KEYS = new Set<string>(["onboarding_call", "q1", "knowhow", "training", "beta", "marketing_call", "q2", "sales", "stripe", "handover"]);

function TestBar(props: {
  steps: RailStep[];
  focus: RailStepKey | null;
  t: (key: string) => string;
  onOpen: (key: RailStepKey) => void;
  onExit: () => void;
}) {
  const sequence = props.steps;
  const index = props.focus
    ? sequence.findIndex((step) => step.key === props.focus)
    : Math.max(0, sequence.findIndex((step) => step.primaryNext));
  const current = sequence[index] ?? null;
  const previous = index > 0 ? sequence[index - 1] : null;
  const next = index < sequence.length - 1 ? sequence[index + 1] : null;
  return (
    <div className="test-bar" role="region" aria-label={props.t("testbar.label")}>
      <span className="test-bar-badge">{props.t("testbar.label")}</span>
      <button className="test-bar-nav" type="button" disabled={!previous} onClick={() => previous && props.onOpen(previous.key)}>
        ← {props.t("testbar.prev")}
      </button>
      <span className="test-bar-step">
        <strong>{current ? props.t(current.labelKey) : ""}</strong>
        <small>{index + 1} / {sequence.length}</small>
      </span>
      <button className="test-bar-nav" type="button" disabled={!next} onClick={() => next && props.onOpen(next.key)}>
        {props.t("testbar.next")} →
      </button>
      <button className="test-bar-exit" type="button" onClick={props.onExit}>{props.t("testbar.exit")}</button>
    </div>
  );
}

type ProgressRailProps = {
  steps: RailStep[];
  profilePhotoUrl?: string | null;
  locale: Locale;
  t: (key: string) => string;
  onSurfaceOpen: (surface: ClientOpenTarget) => void;
  onQuestionnaireOpen: (questionnaire: "q1" | "q2") => void;
  testMode?: boolean;
  onTestOpen?: (key: RailStepKey) => void;
  collapsibleChapters?: boolean;
  currentStepKey?: RailStepKey | null;
  // F39: steps that opened since the client last looked, and the
  // acknowledgement that stops the pulse on the first click.
  freshUnlocked?: string[];
  onStepSeen?: (key: string) => void;
  /**
   * F123 variant switch, for the side-by-side comparison Pavel asked for.
   * "ring" closes the marker's rim in green; "fraction" prints the count
   * inside the circle instead of the padlock. Default is the shipped one.
   */
  substepStyle?: RailSubstepStyle;
};

export type RailSubstepStyle = "ring" | "fraction";

export function MobileStepSwitcher(props: ProgressRailProps & { currentStepKey: RailStepKey | null }) {
  const [open, setOpen] = useState(false);
  const mobileNavTriggerRef = useRef<HTMLButtonElement | null>(null);
  const mobileNavCloseRef = useRef<HTMLButtonElement | null>(null);
  const mobileNavSheetRef = useRef<HTMLDivElement | null>(null);
  const currentIndex = props.currentStepKey
    ? props.steps.findIndex((step) => step.key === props.currentStepKey)
    : -1;
  const current = currentIndex >= 0 ? props.steps[currentIndex] : null;
  const progress = props.t("phase.progress")
    .replace("{current}", String(currentIndex + 1))
    .replace("{total}", String(props.steps.length));
  const close = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    mobileNavCloseRef.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...(mobileNavSheetRef.current?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [href], summary, [tabindex]:not([tabindex="-1"])'
      ) ?? [])];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      window.removeEventListener("keydown", onKeyDown);
      mobileNavTriggerRef.current?.focus();
    };
  }, [open]);

  return (
    <div className="progress-mobile">
      <button className="mobile-step-trigger" type="button" aria-expanded={open} onClick={() => setOpen(true)} ref={mobileNavTriggerRef}>
        <span className="mobile-step-meta">
          <small>{progress}</small>
          <strong>{current ? props.t(current.labelKey) : props.t("home.waiting")}</strong>
        </span>
        <svg className="mobile-step-chevron" viewBox="0 0 20 20" aria-hidden="true">
          <path d="m5 7.5 5 5 5-5" />
        </svg>
      </button>
      {open && (
        <div className="mobile-step-sheet-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
          <div className="mobile-step-sheet" role="dialog" aria-modal="true" aria-labelledby="mobile-step-sheet-title" ref={mobileNavSheetRef}>
            <header className="mobile-step-sheet-header">
              <div>
                <small>{progress}</small>
                <h2 id="mobile-step-sheet-title">{props.t("rail.mobileTitle")}</h2>
              </div>
              <button className="mobile-step-sheet-close" type="button" onClick={close} ref={mobileNavCloseRef} aria-label={props.t("common.close")}>
                <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" /></svg>
              </button>
            </header>
            <ProgressRail
              {...props}
              collapsibleChapters
              currentStepKey={current?.key ?? null}
              onSurfaceOpen={(surface) => {
                close();
                props.onSurfaceOpen(surface);
              }}
              onQuestionnaireOpen={(questionnaire) => {
                close();
                props.onQuestionnaireOpen(questionnaire);
              }}
              onTestOpen={props.onTestOpen ? (key) => {
                close();
                props.onTestOpen?.(key);
              } : undefined}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function ProgressRail({ steps, profilePhotoUrl, t, onSurfaceOpen, onQuestionnaireOpen, testMode, onTestOpen, collapsibleChapters = false, currentStepKey = null, freshUnlocked = [], onStepSeen, substepStyle = "ring" }: ProgressRailProps) {
  const renderStep = (step: RailStep) => {
    const interaction = railInteractionForStep(step);
    const clickable = (testMode && Boolean(onTestOpen)) || interaction.clickable;
    const fresh = freshUnlocked.includes(step.key);
    const openStep = () => {
      // The discovery is over the moment the client acts on it.
      if (fresh) onStepSeen?.(step.key);
      if (testMode && onTestOpen) {
        onTestOpen(step.key);
        return;
      }
      if (!interaction.target) return;
      if (interaction.target === "q1" || interaction.target === "q2") {
        onQuestionnaireOpen(interaction.target);
        return;
      }
      onSurfaceOpen(interaction.target);
    };
    const markerState = step.trainingPhotoPulse
      ? "training"
      : clientStepLocked(step)
        ? "locked"
        : step.state === "done"
          ? "done"
          : "unlocked";
    return (
      <li
        id={`section-${step.key}`}
        className={`progress-item ${step.state} ${step.key === currentStepKey ? "current" : ""}${fresh ? " fresh-unlock" : ""}`}
        data-rail-step={step.key}
        {...(fresh ? { "data-fresh-unlock": "true" } : {})}
        title={[t(`rail.completion.${step.key}`), substepProgressLabel(step.progress, t)]
          .filter(Boolean).join(" · ")}
        key={step.key}
      >
        <RailMarker
          state={markerState}
          profilePhotoUrl={profilePhotoUrl}
          fresh={fresh}
          progress={step.progress}
          substepStyle={substepStyle}
        />
        <span>
          {clickable ? (
            <button className="progress-link" type="button" onClick={openStep}>{t(step.labelKey)}</button>
          ) : <span className="progress-link progress-static">{t(step.labelKey)}</span>}
          <span className="visually-hidden">{t(`rail.status.${step.displayStatus}`)}</span>
          {/* DESIGN.md: a state never travels on colour alone. The arc is the
              quiet half of the fact; this sentence is the spoken half, and it
              also rides the row's title attribute above. */}
          {substepProgressLabel(step.progress, t) && (
            <span className="visually-hidden">{substepProgressLabel(step.progress, t)}</span>
          )}
          <span className="visually-hidden">{t(`rail.completion.${step.key}`)}</span>
        </span>
      </li>
    );
  };
  const buddy = steps.filter((step) => step.chapter === "buddy");
  const sales = steps.filter((step) => step.chapter === "sales");
  const currentChapter = steps.find((step) => step.key === currentStepKey)?.chapter ?? "buddy";

  // Only the mobile sheet folds chapters (limited height, QC-W1f 28.7.). The
  // side rail shows the whole journey at once: every step of both chapters,
  // plain headings, no summaries and no copy under the steps. A round-1 batch
  // hid the steps after the first padlock behind "Dalších N kroků" and printed
  // the unlock sentence in the menu; the unlock sentence belongs to the locked
  // step's own page (LockedStep), not to the navigation.
  // 🔴 F143: a chapter with NO steps is not rendered at all. A sales_only
  // client has no chapter 1, and the heading alone printed "PŘÍPRAVA BUDDYHO"
  // over nothing - a whole phase of the product announced to somebody who
  // never bought it, with an empty space where its steps would be. No
  // structural test could see it; LOOKING at the 1440 shot did.
  if (collapsibleChapters) {
    return (
      <div className="progress-groups">
        {buddy.length > 0 && (
          <details className="progress-chapter" data-rail-chapter="buddy" open={currentChapter === "buddy"}>
            <summary>{t("rail.chapters.buddy")}<span aria-hidden="true">⌄</span></summary>
            <ol className="progress-list home-progress-list">
              {buddy.map(renderStep)}
            </ol>
          </details>
        )}
        {sales.length > 0 && (
          <details className="progress-chapter" data-rail-chapter="sales" open={currentChapter === "sales"}>
            <summary>{t("rail.chapters.sales")}<span aria-hidden="true">⌄</span></summary>
            <ol className="progress-list home-progress-list">
              {sales.map(renderStep)}
            </ol>
          </details>
        )}
      </div>
    );
  }

  return (
    <div className="progress-groups">
      {buddy.length > 0 && (
        <>
          <p className="progress-group-label">{t("rail.chapters.buddy")}</p>
          <ol className="progress-list home-progress-list">
            {buddy.map(renderStep)}
          </ol>
        </>
      )}
      {sales.length > 0 && (
        <>
          <p className="progress-group-label">{t("rail.chapters.sales")}</p>
          <ol className="progress-list home-progress-list">
            {sales.map(renderStep)}
          </ol>
        </>
      )}
    </div>
  );
}

/**
 * F123: the sentence that says the arc out loud. A count step names its
 * fraction ("hotovo 2 ze 3"); a questionnaire deliberately does not - Pavel
 * asked for the ring "bez zlomku" there, and "hotovo 31 ze 45" would be a
 * number nobody reads on a navigation row. Returns null when there is nothing
 * to say, so no empty element is drawn.
 */
export function substepProgressLabel(
  progress: RailStepProgress | null | undefined,
  t: (key: string) => string
): string | null {
  if (!progress || progress.done <= 0) return null;
  if (progress.mode !== "count") return null;
  return t("rail.substeps.done")
    .replace("{done}", String(progress.done))
    .replace("{total}", String(progress.total));
}

/**
 * F123/F130: the step's substeps close as a green arc around the marker.
 *
 * F123 drew it ON the rim (r=14, 2px, inside the marker's own 30px box) and
 * Pavel could not see it: "Ta zelena tretina je hodne spatne videt." Measured,
 * not argued - `artifacts/rail-zlomky/arc-probe.mjs` rasterised the marker and
 * read a WCAG contrast of 2.33 (1440) / 2.43 (390), because a 2px green stroke
 * laid over a 2px #79adff rim is green on blue, the one pairing where the
 * success token has almost nothing to say.
 *
 * F130 moves it into the HALO - the marker's own soft ring between the blue
 * circle and the background ("ten stin mezi backgroundem a tim koleckem"),
 * which `.rail-marker-unlocked` already paints as `0 0 0 4px rgba(74,144,255,
 * .12)`, i.e. 15..19px from the centre. r=17.5 with a 3px stroke lands the
 * band on 16..19: a 1px dark gap keeps it off the rim, so the marker still
 * reads as ONE circle with a ring floating in its own glow, and the green now
 * sits on near-canvas dark instead of on blue.
 */
function RailProgressArc({ progress }: { progress: RailStepProgress }) {
  const fraction = railProgressFraction(progress);
  if (fraction <= 0) return null;
  // r=17.5 + a 3px stroke = the halo band, outside the marker's 30px rim.
  const radius = 17.5;
  const circumference = 2 * Math.PI * radius;
  return (
    <svg
      className="rail-progress-arc"
      viewBox="0 0 38 38"
      focusable="false"
      aria-hidden="true"
      data-progress-fraction={fraction.toFixed(3)}
    >
      <circle
        cx="19"
        cy="19"
        r={radius}
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - fraction)}
      />
    </svg>
  );
}

function RailMarker({ state, profilePhotoUrl, fresh = false, progress = null, substepStyle = "ring" }: {
  state: "locked" | "unlocked" | "done" | "training";
  profilePhotoUrl?: string | null;
  fresh?: boolean;
  progress?: RailStepProgress | null;
  substepStyle?: RailSubstepStyle;
}) {
  if (state === "training") {
    return <WorkingAvatar size="rail" profilePhotoUrl={profilePhotoUrl} />;
  }
  const markerClass = state === "done" ? "check" : state;
  // F39: the open padlock of a freshly unlocked step pulses until the client
  // clicks it. Only the marker animates; the label stays still and readable.
  const freshClass = fresh && state === "unlocked" ? " rail-marker-fresh" : "";
  // Variant B of the F123 comparison: the fraction takes the padlock's place.
  // Only a COUNT step can do it - a questionnaire has no fraction to print.
  const asFraction = substepStyle === "fraction" && progress?.mode === "count" && progress.done > 0;
  const arc = progress && !asFraction ? <RailProgressArc progress={progress} /> : null;
  if (asFraction && progress) {
    return (
      <span
        className={`progress-mark rail-marker rail-marker-${markerClass} rail-marker-fraction${freshClass}`}
        data-marker-state={state}
        data-substep-fraction={`${progress.done}/${progress.total}`}
        aria-hidden="true"
      >
        <span className="rail-fraction-text">{progress.done}<span className="rail-fraction-slash">/</span>{progress.total}</span>
      </span>
    );
  }
  return (
    <span className={`progress-mark rail-marker rail-marker-${markerClass}${freshClass}`} data-marker-state={state} aria-hidden="true">
      {arc}
      {state === "locked" && (
        <svg viewBox="0 0 24 24" focusable="false"><path d="M7.5 10V7.5a4.5 4.5 0 0 1 9 0V10m-10 0h11a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z" /></svg>
      )}
      {state === "unlocked" && (
        <svg viewBox="0 0 24 24" focusable="false"><path d="M9 10V7.5a4.5 4.5 0 0 1 8.45-2.15M6.5 10h11a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z" /></svg>
      )}
      {state === "done" && (
        <svg viewBox="0 0 24 24" focusable="false"><path d="m5 12.5 4.25 4.25L19 7" /></svg>
      )}
    </span>
  );
}

/**
 * K16: what "všechno tu zůstává" actually points at on the finished home. The
 * card's own copy promises the client three things stay theirs (the bot, the
 * sales materials, the payments) and then offered ONE generic button over
 * ~700 px of empty screen - the most triumphant moment of the journey was the
 * emptiest surface in the app. These are the steps that promise names: the
 * four materials and the handover, each already a real destination.
 *
 * Nothing is invented: a step appears only when THIS client's rail says it is
 * done, so a journey that ended without the webinar lists no webinar.
 */
const FINISHED_RETURN_KEYS: RailStepKey[] = ["strategy", "salespage", "emails", "webinar", "handover"];

/**
 * 🔴 F152-W4, found by LOOKING at the 1440 shot: the card's own sentence names
 * four materials ("Strategie, prodejní stránka, e-maily i webinář jsou hotové")
 * and the buttons under it offered three. The sales page step was missing,
 * because a `sales_only` journey ends on the four APPROVED materials while that
 * step keeps its own phases 2 and 3 (order links, publication - F120/F137), so
 * it is `next`, not `done`. The one place the client still has something to do
 * was the one place the finished card would not send them.
 *
 * `locked` is the honest test, and it keeps K16's rule intact ("a journey that
 * ended without the webinar lists no webinar"): a material this client never
 * got is locked or absent from their rail either way. For the full journey the
 * output is unchanged - its end means every section is done, so every one of
 * these steps is `done` too.
 */
export function finishedReturnSteps(railSteps: ReadonlyArray<RailStep>): RailStep[] {
  return FINISHED_RETURN_KEYS
    .map((key) => railSteps.find((step) => step.key === key))
    .filter((step): step is RailStep => Boolean(step) && step!.state !== "locked");
}

export function PrimaryNextAction({
  section,
  workingSection,
  workingStep,
  railSteps = [],
  delivery = null,
  finished,
  journeyMode,
  existingBuddy = false,
  t,
  onQuestionnaireOpen,
  onSurfaceOpen,
  profilePhotoUrl,
  locale,
  video,
  csrfToken,
  onVideoContinue
}: {
  section: SectionDto | null;
  workingSection: string | null;
  workingStep: RailStep | null;
  /** Optional: a caller without a rail has no finished step to point back at. */
  railSteps?: ReadonlyArray<RailStep>;
  /**
   * fix-homereview: the deliverables payload, so the card can tell a material
   * IN REVIEW (the client's turn) from one the team is preparing. A caller
   * without it keeps the old waiting cards.
   */
  delivery?: DeliverablesResponse | null;
  finished: boolean;
  /** F143: which journey ended, so the finished card promises only what it can. */
  journeyMode: JourneyMode;
  /** F168: the Buddy already exists (free build); chapter 1 says "tune", not "build". */
  existingBuddy?: boolean;
  t: (key: string) => string;
  onQuestionnaireOpen: (questionnaire: "q1" | "q2") => void;
  onSurfaceOpen: (surface: DashboardSurface) => void;
  profilePhotoUrl: string | null;
  locale: Locale;
  video: HomeVideoDto | null;
  csrfToken: string;
  onVideoContinue: (stepKey: VideoStepKey) => void | Promise<void>;
}) {
  if (!section) {
    // The whole journey is done: celebrate and point at the results instead
    // of pretending the team still works ("Teď pracuje tým" forever).
    if (finished) {
      const returns = finishedReturnSteps(railSteps);
      // 🔴 F143 (audit N11): the full copy promises the HANDOVER ("celý balík
      // je předaný: bot patří tobě"). A sales_only client's Buddy was never
      // ours to hand over, so those two sentences would describe something
      // that never happened in this system. Same card, same shape, the
      // sentences that are true for THIS client.
      const suffix = journeyMode === "sales_only" ? "SalesOnly" : "";
      return (
        <article className="next-action finished-action">
          <div>
            <p className="card-kicker">{t("home.finished.eyebrow")}</p>
            <h2>{t(`home.finished.title${suffix}`)}</h2>
            <p>{t(`home.finished.copy${suffix}`)}</p>
          </div>
          <button className="secondary-button action-link review-action-button" type="button" onClick={() => onSurfaceOpen("sales")}>
            {t("home.finished.action")}
          </button>
          {returns.length > 0 && (
            <nav className="finished-returns" aria-label={t("home.finished.returnsLabel")}>
              <p className="finished-returns-title">{t("home.finished.returnsTitle")}</p>
              <ul>
                {returns.map((step) => (
                  <li key={step.key}>
                    <button
                      className="finished-return"
                      type="button"
                      data-finished-return={step.key}
                      onClick={() => onSurfaceOpen(step.target as DashboardSurface)}
                    >
                      {t(step.labelKey)}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </article>
      );
    }
    // 🔴 fix-homereview (Dow Janes, Pavel 2026-08-19, measured on prod): a
    // material IN REVIEW never gets a section pointer - the sales section
    // stays `working` forever (F134/S17) - so this card used to fall into the
    // preparing branches below and promise a notification about work that was
    // already DONE and waiting for the client. The rail said "strategy, your
    // turn" while the hero card two centimetres away said "we are preparing
    // your strategy". The ball is with the client, so the card must hand it
    // over: name the material and open its step.
    const reviewStep = reviewReadyStep(railSteps, delivery);
    if (reviewStep) {
      return (
        <article className="next-action" data-review-material={reviewStep.key}>
          <div>
            <p className="card-kicker">{t("home.review.eyebrow")}</p>
            <h2>{t(`home.review.${reviewStep.key}.title`)}</h2>
            <p>{t(`home.review.${reviewStep.key}.copy`)}</p>
          </div>
          <button className="primary-button action-link" type="button" onClick={() => onSurfaceOpen(reviewStep.target as DashboardSurface)}>
            {t("home.review.action")}
          </button>
        </article>
      );
    }
    // No client action pending. Sales production shares the same truthful
    // visual component as the focused strategy page.
    const salesMaterial = workingStep?.state === "working"
      && (workingStep.key === "salespage" || workingStep.key === "emails" || workingStep.key === "webinar")
      ? workingStep.key
      : null;
    if (salesMaterial) {
      return (
        <StrategyWaitingState
          material={salesMaterial}
          profilePhotoUrl={profilePhotoUrl}
          titleKey={`sales.preparingWait.${salesMaterial}.title`}
          copyKey={`sales.preparingWait.${salesMaterial}.copy`}
          etaKey={`sales.preparingWait.${salesMaterial}.eta`}
          t={t}
        />
      );
    }
    if (workingStep?.key === "strategy" || (!workingStep && workingSection === "sales")) {
      return (
        <StrategyWaitingState
          material="strategy"
          profilePhotoUrl={profilePhotoUrl}
          titleKey="sales.strategy.preparingWait.title"
          copyKey="sales.strategy.preparingWait.copy"
          etaKey="sales.strategy.preparingWait.eta"
          t={t}
        />
      );
    }
    // No client action pending. If the team is working on a specific section,
    // name it so the client sees the ball is on our side (law "míč je u nás").
    const title = workingSection ? t(`home.working.${workingSection}.title`) : t("home.waiting");
    const copy = workingSection ? t(`home.working.${workingSection}.copy`) : t("home.waitingCopy");
    // With an uploaded profile photo the training pulse shows the expert's
    // own face becoming the Buddy, not the universal indicator (Pavel).
    return (
      <article className="next-action waiting-action">
        <WorkingAvatar size="card" profilePhotoUrl={profilePhotoUrl} />
        <div>
          <p className="card-kicker">{t("home.workingEyebrow")}</p><h2>{title}</h2><p>{copy}</p>
        </div>
      </article>
    );
  }
  if (section.key === "onboarding_call" || section.key === "marketing_call") {
    // F81: the step IS the gated video - no call dates, no enrollment. When
    // the video is not configured yet, the honest preparing card stands in
    // (and the partner's queue carries the matching "video missing" row).
    return (
      <article className="next-action next-action-call next-action-video">
        <div className="next-action-head">
          <p className="card-kicker">{t("home.primaryEyebrow")}</p>
          <h2>{t(`sections.${section.key}.title`)}</h2>
          <p>{t(`sections.${section.key}.${existingBuddy && section.key === "onboarding_call" ? "existingVideoActionCopy" : "videoActionCopy"}`)}</p>
        </div>
        {video ? (
          <VideoGate
            videoKey={video.key}
            csrfToken={csrfToken}
            t={t}
            onContinue={() => onVideoContinue(section.key as VideoStepKey)}
          />
        ) : (
          <VideoPreparingCard t={t} />
        )}
        <div className="home-call-milestone">
          <span className="step-number-token" aria-hidden="true">02</span>
          <p>{t(`sections.${section.key}.${existingBuddy && section.key === "onboarding_call" ? "existingUnlocksNote" : "unlocksNote"}`)}</p>
        </div>
      </article>
    );
  }
  if (section.key === "q1" || section.key === "q2") {
    return (
      <article className="next-action">
        <div>
          <p className="card-kicker">{t("home.primaryEyebrow")}</p>
          <h2>{t(`sections.${section.key}.title`)}</h2>
          <p>{t(`sections.${section.key}.actionCopy`)}</p>
        </div>
        <button className="primary-button action-link" type="button" onClick={() => onQuestionnaireOpen(section.key === "q1" ? "q1" : "q2")}>
          {t(`sections.${section.key}.action`)}
        </button>
      </article>
    );
  }
  return (
    <article className="next-action">
      <div><p className="card-kicker">{t("home.primaryEyebrow")}</p><h2>{t(`sections.${section.key}.title`)}</h2><p>{t(`sections.${section.key}.actionCopy`)}</p></div>
      <button className="primary-button action-link" type="button" onClick={() => onSurfaceOpen(section.key as M4Surface | M5bSurface)}>{t(`sections.${section.key}.action`)}</button>
    </article>
  );
}

// The steps a client can do WHILE waiting for the call. They belong to the
// call step, so the rail step surface shows them too (F35) instead of hiding
// them behind a trip back to the overview.
export function PreCallSteps(props: {
  profileConfirmed: boolean;
  includesApp: boolean;
  t: (key: string) => string;
  onOpenProfile: () => void;
  onOpenApp: () => void;
}) {
  return (
    <ol className="home-steps-list" start={2}>
      {!props.profileConfirmed && <li className="home-step" data-precall-profile="pending">
        <span className="home-step-num step-number-token" aria-hidden="true">2</span>
        <div className="home-step-body">
          <div className="home-step-title">
            <svg className="home-step-icon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-3.33 0-8 1.67-8 5v2h16v-2c0-3.33-4.67-5-8-5Z"/></svg>
            <strong>{props.t("rail.steps.profile")}</strong>
          </div>
          <p>{props.t("homeSteps.profile.copy")}</p>
          <button className="secondary-button compact-button" type="button" onClick={props.onOpenProfile}>
            {props.t("homeSteps.profile.cta")}
          </button>
        </div>
      </li>}
      {props.includesApp && (
        <li className="home-step">
          <span className="home-step-num step-number-token" aria-hidden="true">3</span>
          <div className="home-step-body">
            <div className="home-step-title">
              <svg className="home-step-icon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M16 2H8a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Zm-4 18.5a1.25 1.25 0 1 1 1.25-1.25A1.25 1.25 0 0 1 12 20.5ZM16 17H8V5h8Z"/></svg>
              <strong>{props.t("rail.steps.apple")}</strong>
            </div>
            <p>{props.t("homeSteps.apple.copy")}</p>
            <button className="secondary-button compact-button" type="button" onClick={props.onOpenApp}>
              {props.t("homeSteps.apple.cta")}
            </button>
          </div>
        </li>
      )}
    </ol>
  );
}

// Home steps are the main content of the early journey. Notifications own
// recurring reminders, so this surface contains no special global strips.

// The invite loop must CLOSE for the owner (Pavel 23.7.: "nepřišla žádná
// pozvánka... hned vidím nějaké chyby"): after sending, the invite stays
// visible as a pending row until the colleague accepts. On dev, where mail
// only reaches the test sink, the server reveals the accept link to hand over.
export function InviteColleague(props: { csrfToken: string; t: (key: string) => string }) {
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteState, setInviteState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  // N03: the seats are capped at three (client_invites capacity assertion), so
  // the server answers 409 `capacity_reached` and a repeat cannot clear it.
  const [inviteIssue, setInviteIssue] = useState<ActionIssue>("generic");
  const [invites, setInvites] = useState<Array<{ id: string; email: string; state: string; link: string | null }>>([]);
  const [revoking, setRevoking] = useState<string | null>(null);

  const loadInvites = useCallback(async () => {
    try {
      const response = await fetch("/api/invites", { credentials: "include" });
      if (!response.ok) return;
      const body = await response.json() as { invites?: Array<{ id: string; email: string; state: string; link?: string | null }> };
      setInvites((body.invites ?? [])
        .filter((item) => item.state === "pending" || item.state === "accepted")
        .map((item) => ({ id: item.id, email: item.email, state: item.state, link: item.link ?? null })));
    } catch { /* the list is best-effort; the form still works */ }
  }, []);

  useEffect(() => {
    void loadInvites();
  }, [loadInvites]);

  async function sendInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setInviteState("sending");
    try {
      const response = await fetch("/api/invites", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Launchpad-CSRF": props.csrfToken, "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ email: inviteEmail.trim() })
      });
      if (!response.ok) {
        setInviteIssue(await actionIssueFrom(response));
        setInviteState("error");
        return;
      }
      setInviteState("sent");
      setInviteEmail("");
      await loadInvites();
    } catch {
      setInviteIssue(actionIssueFromThrown());
      setInviteState("error");
    }
  }

  async function revokeInvite(inviteId: string) {
    setRevoking(inviteId);
    try {
      const response = await fetch(`/api/invites/${encodeURIComponent(inviteId)}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "X-Launchpad-CSRF": props.csrfToken, "Idempotency-Key": crypto.randomUUID() }
      });
      if (!response.ok) {
        setInviteIssue(await actionIssueFrom(response));
        setInviteState("error");
        return;
      }
      await loadInvites();
    } catch {
      setInviteIssue(actionIssueFromThrown());
      setInviteState("error");
    } finally {
      setRevoking(null);
    }
  }

  return (
    <div className="home-invite account-invite">
      <h2>{props.t("invite.accessTitle")}</h2>
      <p>{props.t("invite.accessCopy")}</p>
      <form className="home-invite-form" onSubmit={sendInvite}>
        <label className="visually-hidden" htmlFor="invite-email">{props.t("invite.emailLabel")}</label>
        <input
          id="invite-email"
          type="email"
          inputMode="email"
          placeholder={props.t("invite.emailPlaceholder")}
          value={inviteEmail}
          onChange={(event) => setInviteEmail(event.target.value)}
          required
          disabled={inviteState === "sending"}
        />
        <button className="secondary-button compact-button" type="submit" disabled={inviteState === "sending" || !inviteEmail.trim()}>
          {props.t("invite.button")}
        </button>
      </form>
      {inviteState === "sent" && <p className="message success-message" role="status">{props.t("invite.sent")}</p>}
      {inviteState === "error" && (
        <p className="message error-message" role="alert">
          {props.t(inviteIssue === "generic" ? "invite.error" : actionIssueLocaleKey(inviteIssue))}
        </p>
      )}
      {invites.length > 0 && (
        <ul className="invite-list" aria-label={props.t("invite.pendingTitle")}>
          {invites.map((invite) => (
            <li className="invite-row" key={invite.id}>
              <div className="invite-row-line">
                <strong className="break-text">{invite.email}</strong>
                <span className={`invite-state ${invite.state}`}>
                  {props.t(invite.state === "accepted" ? "invite.stateAccepted" : "invite.statePending")}
                </span>
                <span className="invite-access-note">
                  {props.t(invite.state === "accepted" ? "invite.accessAccepted" : "invite.accessPending")}
                </span>
                {invite.state === "pending" && (
                  <button className="quiet-link" type="button" disabled={revoking === invite.id} onClick={() => void revokeInvite(invite.id)}>
                    {props.t("invite.revoke")}
                  </button>
                )}
              </div>
              {invite.link && (
                <p className="invite-dev-link">
                  {props.t("invite.devLink")}{" "}
                  <a className="break-text" href={invite.link}>{invite.link}</a>
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function HomeSteps(props: {
  includesApp: boolean;
  canInvite: boolean;
  csrfToken: string;
  locale: Locale;
  t: (key: string) => string;
  onOpenProfile: () => void;
  onOpenApp: () => void;
  video: HomeVideoDto | null;
  onVideoContinue: () => void | Promise<void>;
  profileConfirmed?: boolean;
  currentStep?: RailStep | null;
  onboardingStep?: RailStep | null;
}) {
  const onboardingDone = props.onboardingStep?.state === "done";
  const hasCompletedHistory = onboardingDone || props.profileConfirmed;
  const completedHistoryCount = Number(onboardingDone) + Number(Boolean(props.profileConfirmed));
  const hasOpenParallelStep = !props.profileConfirmed || props.includesApp;
  return (
    <section className="home-steps" aria-label={props.t("dashboard.processLabel")}>
      {!onboardingDone && <ol className="home-steps-list">
        {/* Same element the rail step opens (F35): one video experience. */}
        <VideoStepPanel
          stepKey="onboarding_call"
          number={1}
          csrfToken={props.csrfToken}
          t={props.t}
          video={props.video}
          onContinue={props.onVideoContinue}
        />
      </ol>}
      {hasOpenParallelStep && <h2 className="home-steps-subheading">{props.t(onboardingDone ? "homeSteps.afterCall" : "homeSteps.beforeCall")}</h2>}
      {hasOpenParallelStep && (
        <PreCallSteps
          profileConfirmed={Boolean(props.profileConfirmed)}
          includesApp={props.includesApp}
          t={props.t}
          onOpenProfile={props.onOpenProfile}
          onOpenApp={props.onOpenApp}
        />
      )}
      {hasCompletedHistory && (
        <details className="home-completed-history" data-completed-home-history="true">
          <summary>
            <span>{props.t("homeSteps.completedHistory")}</span>
            <span className="home-completed-history-count">
              {translateCount(props.locale, "homeSteps.completedHistoryCount", completedHistoryCount)}
            </span>
          </summary>
          <ol className="home-completed-history-list">
            {onboardingDone && (
              <li className="home-history-row" data-onboarding-call="done">
                <span className="home-step-num step-number-token is-done" aria-hidden="true">1</span>
                <div>
                  <strong>{props.t("sections.onboarding_call.title")}</strong>
                  <span className="status-pill pill-done">{props.t("states.done")}</span>
                  <p>{props.t("homeSteps.call.completedCopy")}</p>
                  <p>{props.t("homeSteps.call.nextAlreadyOpen").replace(
                    "{step}",
                    props.currentStep ? props.t(STEP_HEADING_KEYS[props.currentStep.key]) : props.t("home.waiting")
                  )}</p>
                </div>
              </li>
            )}
            {props.profileConfirmed && (
              /* Closing cards celebrate a finished step the SAME way: the
                 rail's step name, a plain "Hotovo" pill, one sentence and one
                 action - not the old task headline with a sentence-length
                 uppercase pill (audit a1-r5-05). */
              <li className="home-history-row" data-precall-profile="done">
                <span className="home-step-num step-number-token is-done" aria-hidden="true">2</span>
                <div>
                  <strong>{props.t("rail.labels.profile")}</strong>
                  <span className="status-pill pill-done">{props.t("states.done")}</span>
                  <p>{props.t("homeSteps.profile.completedCopy")}</p>
                  <button className="quiet-link" type="button" onClick={props.onOpenProfile}>{props.t("homeSteps.profile.open")}</button>
                </div>
              </li>
            )}
          </ol>
        </details>
      )}
    </section>
  );
}


function formatDate(value: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLanguage(locale), { dateStyle: "medium", timeZone: "Europe/Prague" }).format(new Date(value));
}

function LoadingScreen({ t }: { t: (key: string) => string }) {
  return <main className="state-screen" aria-live="polite"><div className="loading-pulse" /><p>{t("common.loading")}</p></main>;
}

function ErrorScreen({ t, retry }: { t: (key: string) => string; retry: () => Promise<void> }) {
  // A load that never reached the server is a connection drop, not a fault
  // the client can do anything about by repeating blindly (N03).
  return (
    <main className="state-screen">
      <p className="error-message">{t(actionIssueLocaleKey(actionIssueFromThrown()))}</p>
      <button className="primary-button compact-button" type="button" onClick={() => void retry()}>{t("common.retry")}</button>
    </main>
  );
}

function isM5bSurface(value: DashboardSurface): value is M5bSurface {
  return value === "sales" || value === "strategy" || value === "salespage"
    || value === "emails" || value === "webinar" || value === "publish"
    || value === "stripe" || value === "payments" || value === "handover" || value === "app"
    || value === "app:apple" || value === "app:questionnaire" || value === "app:release";
}
